import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

// Small, unobtrusive GDPR-friendly notice. Elevare only sets
// strictly-necessary cookies (auth session + tz preference) — no ad tracking —
// so a single "Got it" acknowledgement is sufficient under GDPR/ePrivacy.
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Small delay so the banner doesn't fight the landing hero animation.
    const t = setTimeout(() => {
      try {
        if (!localStorage.getItem("elevare_cookie_ack")) setVisible(true);
      } catch { /* localStorage disabled — hide banner */ }
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(
        "elevare_cookie_ack",
        JSON.stringify({ v: "2026-08-04", at: new Date().toISOString() }),
      );
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie notice"
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[60] rounded-2xl border border-[#c9a961]/40 bg-[#1e3d3a] text-white shadow-2xl px-5 py-4 backdrop-blur"
      style={{ animation: "cookieSlideIn 0.4s ease-out" }}
    >
      <style>{`
        @keyframes cookieSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className="flex items-start gap-3">
        <Cookie className="h-4 w-4 text-[#c9a961] mt-0.5 shrink-0" strokeWidth={1.6} />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">Cookies</p>
          <p className="mt-1 text-xs leading-relaxed text-white/80">
            We use only strictly-necessary cookies to keep you signed in and remember your time
            zone. No advertising, no third-party tracking. See our{" "}
            <Link
              to="/privacy-policy"
              data-testid="cookie-banner-privacy-link"
              className="underline text-[#c9a961] hover:text-white transition-colors duration-300"
            >
              Privacy Policy
            </Link>.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              data-testid="cookie-banner-accept"
              onClick={dismiss}
              className="inline-flex items-center rounded-full bg-[#c9a961] text-[#1e3d3a] px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-white transition-colors duration-300"
            >
              Got it
            </button>
            <Link
              to="/privacy-policy"
              className="text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-[#c9a961] transition-colors duration-300"
            >
              Learn more
            </Link>
          </div>
        </div>
        <button
          type="button"
          data-testid="cookie-banner-close"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-white/40 hover:text-white transition-colors duration-300 shrink-0"
        >
          <X className="h-4 w-4" strokeWidth={1.6} />
        </button>
      </div>
    </div>
  );
}
